import { Alert, Button, Card, DatePicker, Select, Space, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type InsightProcess = 'admission' | 'enrollment' | 'id_workflow';

type InsightTransition = {
  from_label: string;
  to_label: string;
  count: number;
  median_hours?: number;
  p90_hours?: number;
};

type InsightsPayload = {
  advisory: true;
  process: InsightProcess;
  window: { from: string; to: string };
  transitions: InsightTransition[];
  bottlenecks: InsightTransition[];
  notes?: string[];
};

const { RangePicker } = DatePicker;

export default function ProcessInsightsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [processKey, setProcessKey] = useState<InsightProcess>('admission');
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(90, 'day'), dayjs()]);

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');

  const query = useQuery({
    queryKey: ['education-process-insights', processKey, from, to],
    queryFn: async () =>
      (
        await api.get<InsightsPayload>('/education/process-insights', {
          params: { process: processKey, from, to },
        })
      ).data,
  });

  const data = query.data;
  const hours = (v?: number) => (typeof v === 'number' ? v.toFixed(1) : '—');

  const columns = [
    { title: t('processInsightsFrom'), dataIndex: 'from_label' },
    { title: t('processInsightsTo'), dataIndex: 'to_label' },
    { title: t('count'), dataIndex: 'count', width: 100 },
    {
      title: t('processInsightsMedianHours'),
      dataIndex: 'median_hours',
      width: 140,
      render: (v?: number) => hours(v),
    },
    {
      title: t('processInsightsP90Hours'),
      dataIndex: 'p90_hours',
      width: 140,
      render: (v?: number) => hours(v),
    },
  ];

  return (
    <EducationPageChrome
      title={t('processInsights')}
      description={t('processInsightsIntro')}
      tags={data ? <Tag>{`${data.window.from} → ${data.window.to}`}</Tag> : undefined}
      extra={
        <Space wrap>
          <Select
            style={{ minWidth: 200 }}
            value={processKey}
            onChange={setProcessKey}
            options={[
              { value: 'admission', label: t('processInsightsAdmission') },
              { value: 'enrollment', label: t('processInsightsEnrollment') },
              { value: 'id_workflow', label: t('processInsightsIdWorkflow') },
            ]}
          />
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
          />
          <Button onClick={() => navigate('/education')}>{t('backToHub', { defaultValue: 'Back' })}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('processInsightsAdvisory')} />
        {query.isError && <Alert type="warning" showIcon message={t('processInsightsLoadFailed')} />}

        <Card size="small" title={t('processInsightsBottlenecks')}>
          <Table
            rowKey={(r) => `${r.from_label}->${r.to_label}`}
            size="small"
            loading={query.isLoading}
            pagination={false}
            dataSource={data?.bottlenecks ?? []}
            locale={{ emptyText: t('processInsightsNoBottlenecks') }}
            columns={columns}
          />
        </Card>

        <Card size="small" title={t('processInsightsTransitions')}>
          <Table
            rowKey={(r) => `${r.from_label}->${r.to_label}`}
            size="small"
            loading={query.isLoading}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            dataSource={data?.transitions ?? []}
            locale={{ emptyText: t('processInsightsNoData') }}
            columns={columns}
          />
        </Card>

        {data?.notes && data.notes.length > 0 && (
          <Alert type="warning" showIcon message={data.notes.join(' ')} />
        )}
      </Space>
    </EducationPageChrome>
  );
}
